import { useMemo, useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useContractGameStore } from '../../store/useContractGameStore'
import {
  calculateBlindReward,
} from '../../engine/contractReducer'
import { ALL_SUITS, RANK_ORDER } from '../../data/pokerConfig'
import { getIconifyUrl } from '../../data/cardIcons'
import { PokerCard } from '../cards/PokerCard'
import { JokerRow } from '../cards/JokerRow'
import { PokerLayout } from '../layout/PokerLayout'
import { DeckPile } from '../cards/DeckPile'
import { useDeckInfo } from '../../hooks/useDeckInfo'
import { CardShell, CARD_SIZES } from '../components'
import { useScoringAnimation } from '../../hooks/useScoringAnimation'
import type { FloatingText } from '../../hooks/useScoringAnimation'
import { cn } from '../utils/cn'
import type { PlayingCard, ConsumableCard } from '../../engine/types'
import { BLIND_LABELS, SUIT_DISPLAY, MAX_INTEREST } from '../../data/pokerConfig'

import cardBackImg from '../../assets/images/cards/card-back.webp'
import { useUIEventStore } from '@/shared/stores/uiEventStore'

export function ChallengeBoard() {
  const { t } = useTranslation()
  const state = useContractGameStore()
  const dispatch = useContractGameStore((s) => s.dispatch)

  const {
    challengeSubPhase,
    hand,
    selectedCardIds,
    handsRemaining,
    discardsRemaining,
    runningScore,
    equippedJokers,
    lastHandResult,
    newlyReversedClauseId,
    clauseOutcomes,
    clauseThresholds,
    consumableHand,
    reversedClauseIds,
    clausePool,
    deck: _deck,
    discardPile: _discardPile,
    handTypeLevels: _handTypeLevels,
    playedCards,
    drawnCardIds,
    currentBlind,
    currentBlindIndex,
    blinds,
    bossEffect,
    shopMoney,
    maxJokers,
    maxPlayCards,
    maxConsumables: _maxConsumables,
  } = state

  const scoringCardIds = useMemo(
    () => new Set(lastHandResult?.evaluation.scoringCards.map(c => c.id) ?? []),
    [lastHandResult],
  )
  const [handSortMode, setHandSortMode] = useState<'suit' | 'rank'>('suit')
  const sortedHand = useMemo(() => {
    const suitOrder: Record<string, number> = {}
    ALL_SUITS.forEach((s, i) => { suitOrder[s] = i })
    suitOrder['wild'] = ALL_SUITS.length
    if (handSortMode === 'suit') {
      return [...hand].sort((a, b) => {
        const sd = (suitOrder[a.suit] ?? 99) - (suitOrder[b.suit] ?? 99)
        if (sd !== 0) return sd
        return (RANK_ORDER[b.rank] ?? 0) - (RANK_ORDER[a.rank] ?? 0)
      })
    }
    // rank mode: by rank desc, then suit
    return [...hand].sort((a, b) => {
      const rd = (RANK_ORDER[b.rank] ?? 0) - (RANK_ORDER[a.rank] ?? 0)
      if (rd !== 0) return rd
      return (suitOrder[a.suit] ?? 99) - (suitOrder[b.suit] ?? 99)
    })
  }, [hand, handSortMode])

  // Keyboard shortcuts: 1-8 to toggle hand cards
  useEffect(() => {
    if (challengeSubPhase !== 'CARD_SELECT') return
    const handler = (e: KeyboardEvent) => {
      const idx = parseInt(e.key, 10) - 1
      if (idx < 0 || idx >= sortedHand.length) return
      const card = sortedHand[idx]
      if (!card) return
      const cardId = card.id
      if (selectedCardIds.includes(cardId)) dispatch({ type: 'DESELECT_HAND_CARD', cardId })
      else dispatch({ type: 'SELECT_HAND_CARD', cardId })
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [challengeSubPhase, sortedHand, selectedCardIds, dispatch])

  const { deckSize: deckPileCount, totalSize: totalDeckSize } = useDeckInfo()

  // Scoring animation hook
  const isScoringActive = challengeSubPhase === 'SCORING_ANIMATION' && lastHandResult !== null
  const scoringAnim = useScoringAnimation(
    lastHandResult?.scoringSteps ?? [],
    isScoringActive,
    () => dispatch({ type: 'FINISH_SCORING' }),
  )

  // Card start positions for FLIP animation (hand → center)
  const cardStartPositionsRef = useRef<Record<string, DOMRect>>({})

  const firePlayHand = useUIEventStore((s) => s.firePlayHand)
  const handlePlayHand = useCallback(() => {
    const positions: Record<string, DOMRect> = {}
    for (const cardId of selectedCardIds) {
      const el = document.querySelector(`[data-card-id="${cardId}"]`)
      if (el) positions[cardId] = el.getBoundingClientRect()
    }
    cardStartPositionsRef.current = positions
    firePlayHand()
    dispatch({ type: 'PLAY_HAND' })
  }, [selectedCardIds, dispatch, firePlayHand])

  // Discard ghost animation state
  const [discardGhosts, setDiscardGhosts] = useState<{ card: PlayingCard; rect: DOMRect }[]>([])

  const handleDiscard = useCallback(() => {
    const ghosts: { card: PlayingCard; rect: DOMRect }[] = []
    for (const cardId of selectedCardIds) {
      const el = document.querySelector(`[data-card-id="${cardId}"]`)
      const card = hand.find(c => c.id === cardId)
      if (el && card) ghosts.push({ card, rect: el.getBoundingClientRect() })
    }
    setDiscardGhosts(ghosts)
    dispatch({ type: 'DISCARD_SELECTED' })
    setTimeout(() => setDiscardGhosts([]), 600)
  }, [selectedCardIds, hand, dispatch])

  // ─── Center content per sub-phase ───

  function renderCenterContent() {
    // SCORING_ANIMATION: no center content — scoring info is in sidebar, cards above hand
    if (challengeSubPhase === 'SCORING_ANIMATION') return null

    // BLIND_SELECT: 3-card overview showing all blinds
    if (challengeSubPhase === 'BLIND_SELECT') {
      return (
        <div className="flex flex-col items-center gap-6" style={{ animation: 'fade-in 400ms ease-out' }}>
          {/* Title */}
          <div className="text-center">
            <div className="text-xl font-black tracking-[4px]"
              style={{
                color: '#E8D060',
                fontFamily: "'Noto Serif SC', Georgia, serif",
                textShadow: '0 0 12px rgba(232,208,96,0.3)',
              }}>
              {t('blind.selectTitle')}
            </div>
          </div>

          {/* Three blind cards side by side */}
          <div className="flex gap-4 items-stretch">
            {blinds.map((blind, idx) => {
              const isCurrent = idx === currentBlindIndex
              const isCompleted = idx < currentBlindIndex
              const isLocked = idx > currentBlindIndex
              const isBoss = blind.type === 'boss'
              const blindLabel = BLIND_LABELS[blind.type]
              const targetClauses = blind.clauseIds.map(id => clausePool.find(c => c.id === id)).filter(Boolean)

              return (
                <div
                  key={blind.type}
                  className="relative flex flex-col rounded-xl overflow-hidden"
                  style={{
                    width: 220,
                    background: isCompleted
                      ? 'rgba(0,0,0,0.4)'
                      : isLocked
                        ? 'rgba(0,0,0,0.35)'
                        : isBoss
                          ? 'linear-gradient(180deg, rgba(139,32,32,0.35) 0%, rgba(80,20,20,0.5) 100%)'
                          : 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.4) 100%)',
                    border: isCurrent
                      ? isBoss
                        ? '2px solid #D4849A'
                        : '2px solid #7DAF8B'
                      : isCompleted
                        ? '2px solid rgba(125,175,139,0.3)'
                        : '2px solid rgba(255,255,255,0.08)',
                    boxShadow: isCurrent
                      ? isBoss
                        ? '0 0 24px rgba(212,132,154,0.35), inset 0 0 30px rgba(212,132,154,0.08)'
                        : '0 0 24px rgba(125,175,139,0.3), inset 0 0 30px rgba(125,175,139,0.06)'
                      : '0 4px 16px rgba(0,0,0,0.3)',
                    opacity: isCompleted ? 0.45 : isLocked ? 0.4 : 1,
                    filter: (isCompleted || isLocked) ? 'saturate(0.2) brightness(0.7)' : 'none',
                    animation: `fade-in 300ms ease-out ${idx * 100}ms both`,
                  }}
                >
                  {/* Completed checkmark overlay */}
                  {isCompleted && (
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                      <div
                        className="text-4xl font-black"
                        style={{ color: '#7DAF8B', textShadow: '0 0 12px rgba(125,175,139,0.4)' }}
                      >
                        ✓
                      </div>
                    </div>
                  )}

                  {/* Card header — blind name */}
                  <div
                    className="w-full py-3 px-4 text-center"
                    style={{
                      background: isBoss
                        ? 'linear-gradient(135deg, #8B2020, #A52828)'
                        : blind.type === 'big_blind'
                          ? 'linear-gradient(135deg, #5A4030, #7A5A40)'
                          : 'linear-gradient(135deg, #2D5040, #3D7050)',
                    }}
                  >
                    <div className="text-lg font-black tracking-[3px]"
                      style={{
                        color: isBoss ? '#FFD0D0' : '#E8D060',
                        fontFamily: "'Noto Serif SC', Georgia, serif",
                        textShadow: isBoss
                          ? '0 0 10px rgba(212,132,154,0.4)'
                          : '0 0 10px rgba(232,208,96,0.3)',
                      }}>
                      {blindLabel.cn}
                    </div>
                    <div className="text-[10px] tracking-[2px] mt-0.5"
                      style={{ color: 'rgba(255,255,255,0.4)' }}>
                      {blindLabel.en.toUpperCase()}
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="w-full px-4 py-3 flex flex-col gap-2.5 flex-1">
                    {/* Target clauses */}
                    <div>
                      <div className="text-[9px] uppercase tracking-[2px] mb-1.5"
                        style={{ color: 'rgba(201,169,110,0.5)' }}>
                        {t('blind.targetClauses')}
                      </div>
                      <div className="flex flex-col gap-1">
                        {targetClauses.map(clause => clause && (
                          <div key={clause.id}
                            className="rounded px-2 py-1.5 flex items-center justify-between"
                            style={{
                              background: 'rgba(0,0,0,0.3)',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}>
                            <div className="text-[10px] font-semibold" style={{ color: '#E8E0D0' }}>
                              {clause.shortText}
                            </div>
                            <div className="text-[10px] font-bold font-mono ml-2" style={{ color: '#C9A96E' }}>
                              {clause.threshold.toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Boss effect (always visible on boss card) */}
                    {isBoss && blind.bossEffect && (
                      <div
                        className="rounded-md px-3 py-2 text-center"
                        style={{
                          background: 'rgba(139,32,32,0.25)',
                          border: '1px solid rgba(212,132,154,0.2)',
                        }}
                      >
                        <div className="text-[9px] uppercase tracking-[1.5px] mb-0.5"
                          style={{ color: 'rgba(212,132,154,0.6)' }}>
                          {t('blind.bossEffect')}
                        </div>
                        <div className="text-[11px] font-bold" style={{ color: '#D4849A' }}>
                          {blind.bossEffect.nameCN}
                        </div>
                        <div className="text-[10px] mt-0.5" style={{ color: 'rgba(212,132,154,0.6)' }}>
                          {blind.bossEffect.descriptionCN}
                          {blind.bossEffect.type === 'debuff_suit' && blind.bossEffect.debuffedSuit && (
                            <span className="ml-1 font-bold">
                              ({SUIT_DISPLAY[blind.bossEffect.debuffedSuit].name})
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer — start button or status */}
                  <div className="w-full px-4 pb-3 mt-auto">
                    {isCurrent && (
                      <button
                        className="w-full py-2.5 border-none rounded-md text-white text-[13px] font-bold cursor-pointer"
                        style={{
                          background: isBoss
                            ? 'linear-gradient(135deg, #A03030, #802020)'
                            : 'linear-gradient(135deg, #3D8B4F, #2D7040)',
                          boxShadow: isBoss
                            ? '0 4px 12px rgba(160,48,48,0.4)'
                            : '0 4px 12px rgba(61,139,79,0.4)',
                          border: isBoss
                            ? '2px solid rgba(200,80,80,0.3)'
                            : '2px solid rgba(100,200,120,0.3)',
                        }}
                        onClick={() => dispatch({ type: 'START_BLIND' })}
                      >
                        {t('blind.start')}
                      </button>
                    )}
                    {isCompleted && (
                      <div className="text-center text-[11px] font-semibold py-1"
                        style={{ color: 'rgba(125,175,139,0.6)' }}>
                        {t('blind.completed')}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    // BLIND_COMPLETE: settlement screen — reversed clauses + rewards
    if (challengeSubPhase === 'BLIND_COMPLETE' && currentBlind) {
      const isLastBlind = currentBlindIndex >= blinds.length - 1
      const reversedInBlind = clauseThresholds.filter(t => t.reversed)
      const unreversedInBlind = clauseThresholds.filter(t => !t.reversed)
      const reward = calculateBlindReward(currentBlind.type, handsRemaining, shopMoney)

      return (
        <div className="flex flex-col items-center gap-6 max-w-[520px] mx-auto" style={{ animation: 'fade-in 400ms ease-out' }}>
          {/* Title */}
          <div className="text-center">
            <div
              className="text-2xl font-black tracking-wide"
              style={{ fontFamily: 'var(--font-display)', color: '#C9A96E' }}
            >
              {BLIND_LABELS[currentBlind.type].cn} {t('blind.complete')}
            </div>
            <div className="text-4xl font-black font-mono mt-2"
              style={{ color: '#E8D060', textShadow: '0 0 16px rgba(232,208,96,0.3)', animation: 'score-pop 500ms ease-out 200ms both' }}>
              {runningScore.toLocaleString()}
            </div>
          </div>

          {/* Clause results */}
          <div
            className="w-full rounded-xl px-5 py-4"
            style={{
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(125,175,139,0.3)',
              animation: 'fade-in 300ms ease-out 300ms both',
            }}
          >
            <div className="text-[11px] uppercase tracking-[2px] font-bold text-center mb-3" style={{ color: 'rgba(125,175,139,0.6)' }}>
              {t('blind.clauseResults', 'Clause Results')}
            </div>
            <div className="flex flex-col gap-2">
              {reversedInBlind.map((th) => {
                const clause = clausePool.find(c => c.id === th.clauseId)
                return (
                  <div
                    key={th.clauseId}
                    className="flex items-center gap-3 rounded-lg px-3 py-2"
                    style={{ background: 'rgba(125,175,139,0.12)', border: '1px solid rgba(125,175,139,0.25)' }}
                  >
                    <div className="text-[16px]">&#10003;</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold" style={{ color: '#7DAF8B' }}>
                        {th.clauseId} — {t('blind.reversed', 'Reversed')}
                      </div>
                      <div className="text-[11px] truncate" style={{ color: 'rgba(125,175,139,0.7)' }}>
                        {clause?.shortText}
                      </div>
                    </div>
                    <div className="text-[11px] font-mono shrink-0" style={{ color: 'rgba(125,175,139,0.5)' }}>
                      {th.threshold.toLocaleString()}
                    </div>
                  </div>
                )
              })}
              {unreversedInBlind.map((th) => {
                const clause = clausePool.find(c => c.id === th.clauseId)
                return (
                  <div
                    key={th.clauseId}
                    className="flex items-center gap-3 rounded-lg px-3 py-2"
                    style={{ background: 'rgba(212,132,154,0.08)', border: '1px solid rgba(212,132,154,0.15)' }}
                  >
                    <div className="text-[16px]" style={{ color: 'rgba(212,132,154,0.5)' }}>&#10007;</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold" style={{ color: 'rgba(212,132,154,0.6)' }}>
                        {th.clauseId} — {t('blind.notReversed', 'Not Reversed')}
                      </div>
                      <div className="text-[11px] truncate" style={{ color: 'rgba(212,132,154,0.4)' }}>
                        {clause?.shortText}
                      </div>
                    </div>
                    <div className="text-[11px] font-mono shrink-0" style={{ color: 'rgba(212,132,154,0.3)' }}>
                      {th.threshold.toLocaleString()}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Reward breakdown */}
          <div
            className="w-full rounded-xl px-5 py-4"
            style={{
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(201,169,110,0.3)',
              animation: 'fade-in 300ms ease-out 500ms both',
            }}
          >
            <div className="text-[11px] uppercase tracking-[2px] font-bold text-center mb-3" style={{ color: 'rgba(201,169,110,0.6)' }}>
              {t('blind.rewards', 'Rewards')}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="text-[12px]" style={{ color: 'rgba(180,220,190,0.6)' }}>
                  {t('blind.baseReward', 'Base Reward')}
                </span>
                <span className="text-[14px] font-bold font-mono" style={{ color: '#E8D060' }}>
                  +${reward.baseReward}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12px]" style={{ color: 'rgba(180,220,190,0.6)' }}>
                  {t('blind.handsBonus', 'Remaining Hands')} ({handsRemaining})
                </span>
                <span className="text-[14px] font-bold font-mono" style={{ color: '#7CAFC2' }}>
                  +${reward.handsBonus}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[12px]" style={{ color: 'rgba(180,220,190,0.6)' }}>
                  {t('blind.interest', 'Interest')} (${shopMoney}/5, max {MAX_INTEREST})
                </span>
                <span className="text-[14px] font-bold font-mono" style={{ color: '#7DAF8B' }}>
                  +${reward.interest}
                </span>
              </div>
              <div className="h-px mt-1 mb-1" style={{ background: 'rgba(201,169,110,0.2)' }} />
              <div className="flex justify-between items-center">
                <span className="text-[13px] font-bold" style={{ color: '#C9A96E' }}>
                  {t('blind.total', 'Total')}
                </span>
                <span className="text-[18px] font-black font-mono" style={{ color: '#E8D060', textShadow: '0 0 8px rgba(232,208,96,0.3)' }}>
                  +${reward.total}
                </span>
              </div>
            </div>
          </div>

          {/* Action button → shop → then blind select */}
          {isLastBlind ? (
            <button
              className="py-3 px-10 border-none rounded-md text-white text-[15px] font-bold cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #D4849A, #C48B9E)',
                boxShadow: '0 4px 12px rgba(212,132,154,0.3)',
                animation: 'fade-in 300ms ease-out 700ms both',
              }}
              onClick={() => dispatch({ type: 'ADVANCE_PRESENTATION' })}
            >
              {t('challenge.viewResults')}
            </button>
          ) : (
            <button
              className="py-3 px-10 border-none rounded-md text-white text-[15px] font-bold cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #3D8B4F, #2D7040)',
                boxShadow: '0 4px 16px rgba(61,139,79,0.4)',
                border: '2px solid rgba(100,200,120,0.3)',
                animation: 'fade-in 300ms ease-out 700ms both',
              }}
              onClick={() => dispatch({ type: 'CONTINUE_TO_SHOP' })}
            >
              {t('blind.toShop')}
            </button>
          )}
        </div>
      )
    }

    if (challengeSubPhase === 'CLAUSE_REVERSED' && newlyReversedClauseId) {
      const clause = clausePool.find(c => c.id === newlyReversedClauseId)
      const outcome = clauseOutcomes.find(o => o.clauseId === newlyReversedClauseId)
      const isBoss = clause?.isBoss

      return (
        <div className="flex flex-col items-center gap-4">
          <div
            className={cn('py-3.5 px-8 rounded-md text-center', isBoss ? 'text-[#4A3F5C]' : 'text-white')}
            style={{
              background: isBoss ? 'linear-gradient(135deg, #C9A96E, #E8C860)' : '#7DAF8B',
              animation: 'slam-down 500ms ease-out both',
            }}
          >
            <div className="text-xl font-extrabold font-heading tracking-[2px]">
              {isBoss ? t('challenge.fullReversal') : t('challenge.clauseReversed')}
            </div>
          </div>
          {clause && (
            <div
              className="p-3 max-w-[380px] text-center rounded-lg"
              style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)', animation: 'fade-in 300ms ease-out 300ms both' }}
            >
              <span className="text-[10px] font-mono" style={{ color: 'rgba(180,220,190,0.4)' }}>{clause.id}</span>
              <div className="text-sm font-semibold mt-1" style={{ color: '#E8E0D0' }}>{clause.shortText}</div>
            </div>
          )}
          {outcome && outcome.trackChanges && Object.keys(outcome.trackChanges).length > 0 && (
            <div className="flex gap-3" style={{ animation: 'fade-in 300ms ease-out 500ms both' }}>
              {Object.entries(outcome.trackChanges).map(([k, v]) => (
                <span key={k} className="text-sm font-bold font-mono" style={{ color: (v as number) > 0 ? '#7DAF8B' : '#D4849A' }}>
                  {k} {(v as number) > 0 ? '+' : ''}{v as number}
                </span>
              ))}
            </div>
          )}
          <ContinueButton onClick={() => dispatch({ type: 'ADVANCE_PRESENTATION' })} delay={600} />
        </div>
      )
    }

    if (challengeSubPhase === 'FINAL_TALLY') {
      const totalReversed = reversedClauseIds.length
      const totalClauses = blinds.reduce((sum, b) => sum + b.clauseIds.length, 0)

      return (
        <div className="flex flex-col items-center gap-5">
          <div className="text-base font-semibold" style={{ color: 'rgba(180,220,190,0.7)', animation: 'fade-in 300ms ease-out' }}>
            {t('challenge.tallyComplete')}
          </div>
          <div className="flex gap-6 text-center" style={{ animation: 'fade-in 300ms ease-out 400ms both' }}>
            <div>
              <div className="text-[11px]" style={{ color: 'rgba(180,220,190,0.4)' }}>{t('challenge.reversedCount')}</div>
              <div className="text-xl font-bold" style={{ color: '#7DAF8B' }}>{totalReversed}</div>
            </div>
            <div>
              <div className="text-[11px]" style={{ color: 'rgba(180,220,190,0.4)' }}>{t('challenge.totalClauses')}</div>
              <div className="text-xl font-bold" style={{ color: 'rgba(180,220,190,0.6)' }}>{totalClauses}</div>
            </div>
          </div>
          <button
            className="py-3 px-10 border-none rounded-md text-white text-[15px] font-bold cursor-pointer"
            style={{ background: 'linear-gradient(135deg, #D4849A, #C48B9E)', boxShadow: '0 4px 12px rgba(212,132,154,0.3)', animation: 'fade-in 300ms ease-out 600ms both' }}
            onClick={() => dispatch({ type: 'ADVANCE_PRESENTATION' })}
          >
            {t('challenge.viewResults')}
          </button>
        </div>
      )
    }

    return null
  }

  // ─── Sidebar header ───

  const sidebarHeader = challengeSubPhase === 'BLIND_SELECT' ? (
    <div className="mx-3 mt-4 mb-3 rounded-lg px-3 py-3 text-center"
      style={{ background: 'rgba(0,0,0,0.25)', border: '2px solid rgba(201,169,110,0.3)' }}
    >
      <div className="text-2xl font-black tracking-[4px]"
        style={{
          color: '#E8D060',
          fontFamily: "var(--font-display)",
          textShadow: '0 0 10px rgba(232,208,96,0.3)',
        }}>
        {t('blind.selectTitle')}
      </div>
    </div>
  ) : currentBlind ? (
    <div className="mx-3 mt-4 mb-3 rounded-lg px-3 py-3 text-center"
      style={{
        background: currentBlind.type === 'boss'
          ? 'linear-gradient(135deg, rgba(139,32,32,0.4), rgba(100,20,20,0.5))'
          : 'rgba(0,0,0,0.25)',
        border: currentBlind.type === 'boss'
          ? '2px solid rgba(212,132,154,0.4)'
          : '2px solid rgba(201,169,110,0.3)',
      }}
    >
      <div className="text-2xl font-black tracking-[3px]"
        style={{
          color: currentBlind.type === 'boss' ? '#FFD0D0' : '#E8D060',
          fontFamily: "var(--font-display)",
          textShadow: currentBlind.type === 'boss'
            ? '0 0 10px rgba(212,132,154,0.4)'
            : '0 0 10px rgba(232,208,96,0.3)',
        }}>
        {currentBlind.label}
      </div>
      {bossEffect && (
        <div className="text-[9px] mt-1" style={{ color: 'rgba(212,132,154,0.6)' }}>
          {bossEffect.nameCN}
          {bossEffect.type === 'debuff_suit' && bossEffect.debuffedSuit && (
            <span className="ml-0.5">({SUIT_DISPLAY[bossEffect.debuffedSuit].name})</span>
          )}
        </div>
      )}
    </div>
  ) : null

  // ─── Layout ───

  return (
    <PokerLayout
      sidebarHeader={sidebarHeader}
      showCodeRain={currentBlind?.type !== 'boss'}
      showBossFluid={currentBlind?.type === 'boss'}
      animationChips={isScoringActive ? scoringAnim.displayChips : undefined}
      animationMult={isScoringActive ? scoringAnim.displayMult : undefined}
      isAnimating={isScoringActive}
      animationHandType={isScoringActive ? scoringAnim.handTypeCN : undefined}
      animationHandLevel={isScoringActive ? scoringAnim.handTypeLevel : undefined}
      animationPhase={isScoringActive ? scoringAnim.phase : undefined}
    >
      {/* Top row: Jokers (left) + Consumables (right) */}
      <div className="relative z-10 flex items-start">
        <div className="flex-1 min-w-0">
          <JokerRow
            jokers={equippedJokers}
            maxJokers={maxJokers}
            activeJokerId={isScoringActive ? scoringAnim.activeJokerId : null}
            effects={isScoringActive ? lastHandResult?.jokerEffects : undefined}
          />
        </div>
        <ConsumableRow
          consumables={consumableHand}
          selectedCardIds={selectedCardIds}
          onUse={(consumableId, targetCardIds) =>
            dispatch({ type: 'USE_CONSUMABLE', consumableId, targetCardIds })
          }
        />
      </div>

      {/* Center content — varies by sub-phase */}
      <div className="flex-1 flex items-center justify-center relative z-10">
        {challengeSubPhase === 'SCORING_ANIMATION' && playedCards.length > 0 ? (
          <PlayedCardsRow
            cards={playedCards}
            activeCardId={scoringAnim.activeCardId}
            phase={scoringAnim.phase}
            floatingTexts={scoringAnim.floatingTexts}
            startPositions={cardStartPositionsRef.current}
            scoringCardIds={scoringCardIds}
          />
        ) : (
          renderCenterContent()
        )}
      </div>

      {/* Hand cards fan — visible during CARD_SELECT and SCORING_ANIMATION */}
      {(challengeSubPhase === 'CARD_SELECT' || challengeSubPhase === 'SCORING_ANIMATION') && hand.length > 0 && (
        <HandFan
          cards={sortedHand}
          selectedCardIds={challengeSubPhase === 'CARD_SELECT' ? selectedCardIds : []}
          onToggle={(cardId) => {
            if (challengeSubPhase !== 'CARD_SELECT') return
            if (selectedCardIds.includes(cardId)) dispatch({ type: 'DESELECT_HAND_CARD', cardId })
            else dispatch({ type: 'SELECT_HAND_CARD', cardId })
          }}
          maxSelect={maxPlayCards}
          disabled={challengeSubPhase === 'SCORING_ANIMATION'}
          activeCardId={challengeSubPhase === 'SCORING_ANIMATION' ? scoringAnim.activeCardId : null}
          drawnCardIds={challengeSubPhase === 'CARD_SELECT' ? drawnCardIds : []}
          sortMode={handSortMode}
          onSortChange={challengeSubPhase === 'CARD_SELECT' ? setHandSortMode : undefined}
        />
      )}

      {/* Action bar — visible during CARD_SELECT */}
      {challengeSubPhase === 'CARD_SELECT' && (
        <div className="relative z-10 flex items-center justify-center gap-3 pb-4 pt-1">
          {/* Play hand button */}
          <button
            className={cn(
              'py-2.5 px-8 border-none rounded-md text-white text-sm font-bold cursor-pointer transition-opacity',
              (selectedCardIds.length === 0
                || selectedCardIds.length > maxPlayCards
                || handsRemaining <= 0
                || (bossEffect?.type === 'must_play_5' && selectedCardIds.length !== 5))
                && 'opacity-40 cursor-not-allowed',
            )}
            style={{
              background: 'linear-gradient(135deg, #3D8B4F, #2D7040)',
              boxShadow: '0 4px 16px rgba(61,139,79,0.4)',
              border: '2px solid rgba(100,200,120,0.3)',
            }}
            onClick={handlePlayHand}
            disabled={
              selectedCardIds.length === 0
              || selectedCardIds.length > maxPlayCards
              || handsRemaining <= 0
              || (bossEffect?.type === 'must_play_5' && selectedCardIds.length !== 5)
            }
          >
            {t('challenge.playHand')}
            {bossEffect?.type === 'must_play_5' && selectedCardIds.length !== 5 && selectedCardIds.length > 0 && (
              <span className="text-[10px] ml-1 opacity-70">(5)</span>
            )}
          </button>

          {/* Discard button */}
          <button
            className={cn(
              'py-2.5 px-6 rounded-md text-sm font-semibold cursor-pointer transition-opacity',
              (selectedCardIds.length === 0 || discardsRemaining <= 0) && 'opacity-40 cursor-not-allowed',
            )}
            style={{ background: 'rgba(0,0,0,0.3)', border: '2px solid rgba(212,132,154,0.3)', color: '#D4849A' }}
            onClick={handleDiscard}
            disabled={selectedCardIds.length === 0 || discardsRemaining <= 0}
          >
            {t('challenge.discard')}
          </button>
        </div>
      )}

      {/* Deck pile — bottom right */}
      <DeckPile deckSize={deckPileCount} totalSize={totalDeckSize} />

      {/* Discard ghost overlay — cards flying from hand to deck pile */}
      {discardGhosts.length > 0 && (
        <DiscardGhostOverlay ghosts={discardGhosts} />
      )}

    </PokerLayout>
  )
}

// =============================================================================
// PlayedCardsRow — horizontal row of played cards during SCORING_ANIMATION
// =============================================================================

function PlayedCardsRow({
  cards,
  activeCardId,
  phase,
  floatingTexts = [],
  startPositions = {},
  scoringCardIds = new Set<string>(),
}: {
  cards: PlayingCard[]
  activeCardId: string | null
  phase: string
  floatingTexts?: FloatingText[]
  startPositions?: Record<string, DOMRect>
  scoringCardIds?: Set<string>
}) {
  const cardSpacing = Math.min(130, 900 / Math.max(cards.length, 1))
  const innerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const outerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const hasKickers = cards.some(c => !scoringCardIds.has(c.id))

  // FLIP animation: cards fly from hand to center one by one
  const DEAL_STAGGER = 120
  const DEAL_FLIGHT = 500

  useLayoutEffect(() => {
    if (phase !== 'cards_spread' || Object.keys(startPositions).length === 0) return

    // Set all cards at their hand positions, hidden
    cards.forEach((card) => {
      const el = innerRefs.current[card.id]
      const startRect = startPositions[card.id]
      if (!el || !startRect) return

      const endRect = el.getBoundingClientRect()
      const dx = (startRect.left + startRect.width / 2) - (endRect.left + endRect.width / 2)
      const dy = (startRect.top + startRect.height / 2) - (endRect.top + endRect.height / 2)

      el.style.transform = `translate(${dx}px, ${dy}px)`
      el.style.opacity = '0'
      el.style.transition = 'none'
    })

    void document.body.offsetHeight

    // Fly each card to table one at a time
    requestAnimationFrame(() => {
      cards.forEach((card, i) => {
        const el = innerRefs.current[card.id]
        if (!el) return
        const stagger = i * DEAL_STAGGER
        el.style.transition = `transform ${DEAL_FLIGHT}ms cubic-bezier(0.22, 1, 0.36, 1) ${stagger}ms, opacity 150ms ease-out ${stagger}ms`
        el.style.transform = 'translate(0, 0)'
        el.style.opacity = '1'
      })
    })

    const cleanupDelay = DEAL_FLIGHT + cards.length * DEAL_STAGGER + 50
    const timer = setTimeout(() => {
      cards.forEach((card) => {
        const el = innerRefs.current[card.id]
        if (!el) return
        el.style.transform = ''
        el.style.opacity = ''
        el.style.transition = ''
      })
    }, cleanupDelay)

    return () => clearTimeout(timer)
  }, [phase, cards, startPositions])

  // Collect animation: cards fly to score panel on the left and shrink
  useLayoutEffect(() => {
    if (phase !== 'cards_collect') return

    const scorePanel = document.querySelector('[data-score-panel]')
    if (!scorePanel) return
    const targetRect = scorePanel.getBoundingClientRect()
    const targetCx = targetRect.left + targetRect.width / 2
    const targetCy = targetRect.top + targetRect.height / 2

    cards.forEach((card, i) => {
      const outer = outerRefs.current[card.id]
      if (!outer) return

      const cardRect = outer.getBoundingClientRect()
      const cardCx = cardRect.left + cardRect.width / 2
      const cardCy = cardRect.top + cardRect.height / 2
      const dx = targetCx - cardCx
      const dy = targetCy - cardCy

      outer.style.transition = 'none'
      outer.style.transform = outer.style.transform || `translateX(${(i - (cards.length - 1) / 2) * cardSpacing}px)`

      void outer.offsetHeight

      const stagger = i * 50
      outer.style.transition = `transform 500ms cubic-bezier(0.4, 0, 0.2, 1) ${stagger}ms, opacity 400ms ease-in ${stagger + 100}ms`
      outer.style.transform = `translate(${dx}px, ${dy}px) scale(0.15)`
      outer.style.opacity = '0'
    })
  }, [phase, cards, cardSpacing])

  return (
    <div className="relative z-10 flex items-center justify-center" style={{ minHeight: 200 }}>
      <div className="relative flex items-center justify-center" style={{ height: 170 }}>
        {cards.map((card, i) => {
          const isScoring = scoringCardIds.has(card.id)
          const isActive = card.id === activeCardId
          const activeIdx = cards.findIndex(c => c.id === activeCardId)
          // Distinction between scoring/kicker only after hand_type_reveal
          const showDistinction = phase !== 'cards_spread' && phase !== 'hand_type_reveal'
          const isScored = showDistinction && phase !== 'cards_collect' && !isActive
            && activeIdx >= 0 && i < activeIdx
          const isJokerShaking = phase === 'joker_scoring' && activeCardId === card.id
          const isKickerDimmed = showDistinction && hasKickers && !isScoring

          const xOffset = (i - (cards.length - 1) / 2) * cardSpacing
          // Scoring cards elevated above kickers — only after distinction phase
          const yOffset = showDistinction && hasKickers ? (isScoring ? -16 : 16) : 0

          // Filter floating texts targeting this card
          const cardFloats = floatingTexts.filter(ft => ft.targetId === card.id)

          return (
            <div
              key={card.id}
              ref={(el) => { outerRefs.current[card.id] = el }}
              className="absolute"
              style={{
                transform: `translateX(${xOffset}px) translateY(${yOffset}px)`,
                zIndex: isActive ? 20 : i,
                opacity: isKickerDimmed ? 0.55 : isScored ? 0.5 : 1,
                filter: isKickerDimmed ? 'brightness(0.7)' : isScored ? 'brightness(0.7)' : isActive ? 'brightness(1.15)' : undefined,
                transition: 'transform 400ms ease-out, opacity 300ms, filter 300ms',
              }}
            >
              {/* Floating texts above this card */}
              {cardFloats.length > 0 && (
                <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ bottom: '100%', marginBottom: 4, zIndex: 30 }}>
                  {cardFloats.map((ft, fi) => (
                    <div
                      key={ft.id}
                      className="text-lg font-black font-mono whitespace-nowrap text-center"
                      style={{
                        color: ft.color,
                        animation: ft.color === '#7CAFC2'
                          ? 'float-chip-value 1200ms ease-out forwards'
                          : 'float-mult-value 1200ms ease-out forwards',
                        textShadow: `0 0 8px ${ft.color}40`,
                        marginTop: fi > 0 ? -4 : 0,
                      }}
                    >
                      {ft.text}
                    </div>
                  ))}
                </div>
              )}

              {/* Inner wrapper: FLIP target during cards_spread, CSS animation otherwise */}
              <div
                ref={(el) => { innerRefs.current[card.id] = el }}
                style={{
                  animation: phase !== 'cards_spread' && phase !== 'cards_collect'
                    ? isActive
                      ? 'card-score-pulse 500ms ease-out'
                      : isJokerShaking
                        ? 'card-joker-shake 400ms ease-out'
                        : undefined
                    : undefined,
                }}
              >
                <PokerCard
                  card={card}
                  selected={false}
                  size="sm"
                  index={0}
                  disabled
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// HandFan — arc card layout
// =============================================================================

function HandFan({
  cards,
  selectedCardIds,
  onToggle,
  maxSelect,
  disabled = false,
  activeCardId = null,
  drawnCardIds = [],
  sortMode,
  onSortChange,
}: {
  cards: PlayingCard[]
  selectedCardIds: string[]
  onToggle: (cardId: string) => void
  maxSelect: number
  disabled?: boolean
  activeCardId?: string | null
  drawnCardIds?: string[]
  sortMode?: 'suit' | 'rank'
  onSortChange?: (mode: 'suit' | 'rank') => void
}) {
  const { t } = useTranslation()
  const totalCards = cards.length
  const drawPosRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const drawFlipRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Deal animation: cards fly from deck pile + 3D flip from back to front
  useLayoutEffect(() => {
    if (drawnCardIds.length === 0) return

    const deckEl = document.querySelector('[data-deck-pile]')
    if (!deckEl) return
    const deckRect = deckEl.getBoundingClientRect()

    // Set initial state: each drawn card starts at deck position, showing back
    drawnCardIds.forEach((cardId) => {
      const posEl = drawPosRefs.current[cardId]
      const flipEl = drawFlipRefs.current[cardId]
      if (!posEl || !flipEl) return

      const cardRect = posEl.getBoundingClientRect()
      const dx = (deckRect.left + deckRect.width / 2) - (cardRect.left + cardRect.width / 2)
      const dy = (deckRect.top + deckRect.height / 2) - (cardRect.top + cardRect.height / 2)

      posEl.style.transform = `translate(${dx}px, ${dy}px) scale(0.7)`
      posEl.style.opacity = '0'
      posEl.style.transition = 'none'
      flipEl.style.transform = 'rotateY(180deg)'
      flipEl.style.transition = 'none'
    })

    void document.body.offsetHeight

    // Animate: fly to hand position + flip to front face
    requestAnimationFrame(() => {
      drawnCardIds.forEach((cardId, i) => {
        const posEl = drawPosRefs.current[cardId]
        const flipEl = drawFlipRefs.current[cardId]
        if (!posEl || !flipEl) return

        const stagger = i * 150
        // Position: fly from deck to hand
        posEl.style.transition = `transform 550ms cubic-bezier(0.22, 1, 0.36, 1) ${stagger}ms, opacity 150ms ease-out ${stagger}ms`
        posEl.style.transform = 'translate(0, 0) scale(1)'
        posEl.style.opacity = '1'
        // Flip: back → front (starts mid-flight)
        flipEl.style.transition = `transform 400ms ease-out ${stagger + 200}ms`
        flipEl.style.transform = 'rotateY(0deg)'
      })
    })

    // Cleanup inline styles after all animations complete
    const totalDuration = 550 + drawnCardIds.length * 150 + 250
    const timer = setTimeout(() => {
      drawnCardIds.forEach((cardId) => {
        const posEl = drawPosRefs.current[cardId]
        const flipEl = drawFlipRefs.current[cardId]
        if (posEl) {
          posEl.style.transform = ''
          posEl.style.opacity = ''
          posEl.style.transition = ''
        }
        if (flipEl) {
          flipEl.style.transform = ''
          flipEl.style.transition = ''
        }
      })
    }, totalDuration)

    return () => clearTimeout(timer)
  }, [drawnCardIds])

  if (totalCards === 0) {
    return (
      <div className="relative z-10 flex items-center justify-center py-8">
        <span className="text-sm" style={{ color: 'rgba(180,220,190,0.35)' }}>{t('challenge.noCards')}</span>
      </div>
    )
  }

  const selectedSet = new Set(selectedCardIds)
  const drawnSet = new Set(drawnCardIds)
  const selectedCount = selectedCardIds.length
  const canSelect = selectedCount < maxSelect
  const maxAngle = Math.min(15, totalCards * 1.8)
  const cardSpacing = Math.min(90, 800 / totalCards)
  // Arc: edge cards sink down, center stays high (parabola)
  const arcHeight = Math.min(35, totalCards * 3)

  return (
    <div className="relative z-10 pb-2">
      {!disabled && (
        <div className="flex items-center justify-center gap-3 mb-1">
          <span className="text-[10px] font-mono" style={{ color: 'rgba(180,220,190,0.35)' }}>
            {selectedCount > 0 && t('challenge.selectedCount', { count: selectedCount, max: maxSelect })}
            {t('challenge.handCards', { count: totalCards })}
          </span>
          {onSortChange && sortMode && (
            <div className="flex gap-0.5 rounded-md overflow-hidden" style={{ border: '1px solid rgba(180,220,190,0.15)' }}>
              <button
                className="px-2 py-0.5 text-[10px] font-semibold cursor-pointer border-none"
                style={{
                  background: sortMode === 'suit' ? 'rgba(180,220,190,0.15)' : 'transparent',
                  color: sortMode === 'suit' ? 'rgba(180,220,190,0.7)' : 'rgba(180,220,190,0.3)',
                }}
                onClick={() => onSortChange('suit')}
              >
                {t('challenge.sortSuit', 'Suit')}
              </button>
              <button
                className="px-2 py-0.5 text-[10px] font-semibold cursor-pointer border-none"
                style={{
                  background: sortMode === 'rank' ? 'rgba(180,220,190,0.15)' : 'transparent',
                  color: sortMode === 'rank' ? 'rgba(180,220,190,0.7)' : 'rgba(180,220,190,0.3)',
                }}
                onClick={() => onSortChange('rank')}
              >
                {t('challenge.sortRank', 'Rank')}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="relative flex items-end justify-center" style={{ height: 220, paddingBottom: 8 }}>
        {cards.map((card, i) => {
          const isSelected = selectedSet.has(card.id)
          const isDrawn = drawnSet.has(card.id)
          const isHeldActive = activeCardId === card.id
          const angle = totalCards <= 1 ? 0 : -maxAngle + (2 * maxAngle * i) / (totalCards - 1)
          const xOffset = (i - (totalCards - 1) / 2) * cardSpacing
          // Parabolic arc: normalized -1..1, center=0 edge=1
          const norm = totalCards <= 1 ? 0 : (2 * i / (totalCards - 1)) - 1
          const yArc = -arcHeight * (1 - norm * norm)  // negative at center (up), 0 at edges

          return (
            <div
              key={card.id}
              data-card-id={card.id}
              style={{
                position: 'absolute',
                transform: `translateX(${xOffset}px) translateY(${yArc + (isSelected ? -32 : 0)}px) rotate(${angle}deg)`,
                transformOrigin: 'bottom center',
                transition: 'transform 200ms ease-out',
                zIndex: isSelected ? 20 + i : isHeldActive ? 20 : i,
              }}
            >
              {isDrawn ? (
                /* Deal animation: 3D flip wrapper (back → front) */
                <div style={{ perspective: 800 }}>
                  <div
                    ref={(el) => { drawPosRefs.current[card.id] = el }}
                  >
                    <div
                      ref={(el) => { drawFlipRefs.current[card.id] = el }}
                      style={{ transformStyle: 'preserve-3d', position: 'relative' }}
                    >
                      {/* Front face */}
                      <div style={{ backfaceVisibility: 'hidden' }}>
                        <PokerCard
                          card={card}
                          selected={isSelected}
                          size="sm"
                          index={0}
                          disabled={disabled || (!isSelected && !canSelect)}
                          onClick={() => !disabled && onToggle(card.id)}
                        />
                      </div>
                      {/* Back face */}
                      <img
                        src={cardBackImg}
                        alt=""
                        className="absolute top-0 left-0 rounded-[10px]"
                        style={{
                          width: CARD_SIZES.sm.width,
                          height: CARD_SIZES.sm.height,
                          backfaceVisibility: 'hidden',
                          transform: 'rotateY(180deg)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Normal card (not freshly drawn) */
                <div
                  style={{
                    animation: isHeldActive
                      ? 'card-score-pulse 500ms ease-out'
                      : undefined,
                  }}
                >
                  <PokerCard
                    card={card}
                    selected={isSelected}
                    size="sm"
                    index={0}
                    disabled={disabled || (!isSelected && !canSelect)}
                    onClick={() => !disabled && onToggle(card.id)}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// =============================================================================
// DiscardGhostOverlay — ghost cards flying from hand to deck pile on discard
// =============================================================================

function DiscardGhostOverlay({ ghosts }: { ghosts: { card: PlayingCard; rect: DOMRect }[] }) {
  const ghostRefs = useRef<Record<number, HTMLDivElement | null>>({})

  useLayoutEffect(() => {
    const deckEl = document.querySelector('[data-deck-pile]')
    if (!deckEl) return
    const deckRect = deckEl.getBoundingClientRect()
    const targetCx = deckRect.left + deckRect.width / 2
    const targetCy = deckRect.top + deckRect.height / 2

    ghosts.forEach((ghost, i) => {
      const el = ghostRefs.current[i]
      if (!el) return

      const ghostCx = ghost.rect.left + ghost.rect.width / 2
      const ghostCy = ghost.rect.top + ghost.rect.height / 2
      const dx = targetCx - ghostCx
      const dy = targetCy - ghostCy

      el.style.transition = 'none'
      el.style.transform = 'scale(1)'
      el.style.opacity = '1'

      void el.offsetHeight

      const stagger = i * 60
      el.style.transition = `transform 450ms cubic-bezier(0.4, 0, 0.2, 1) ${stagger}ms, opacity 350ms ease-in ${stagger + 100}ms`
      el.style.transform = `translate(${dx}px, ${dy}px) scale(0.3) rotate(15deg)`
      el.style.opacity = '0'
    })
  }, [ghosts])

  return (
    <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 100 }}>
      {ghosts.map((ghost, i) => (
        <div
          key={`discard-ghost-${i}`}
          ref={(el) => { ghostRefs.current[i] = el }}
          style={{
            position: 'fixed',
            left: ghost.rect.left,
            top: ghost.rect.top,
            width: ghost.rect.width,
            height: ghost.rect.height,
          }}
        >
          <PokerCard card={ghost.card} selected={false} size="sm" index={0} disabled />
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// ConsumableRow — top right, same row as Jokers
// =============================================================================

function ConsumableRow({
  consumables,
  selectedCardIds,
  onUse,
}: {
  consumables: ConsumableCard[]
  selectedCardIds: string[]
  onUse: (consumableId: string, targetCardIds: string[]) => void
}) {
  const { t } = useTranslation()
  const MAX_VISIBLE = 2
  const visibleConsumables = consumables.slice(0, MAX_VISIBLE)
  const emptySlots = Math.max(0, MAX_VISIBLE - visibleConsumables.length)
  return (
    <div
      className="flex items-center gap-2 px-3 py-3 mr-4 mt-3 rounded-lg shrink-0"
      style={{
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(212,132,154,0.15)',
      }}
    >
      <div className="text-[11px] uppercase tracking-wider shrink-0 font-semibold" style={{ color: 'rgba(212,132,154,0.45)' }}>
        {t('challenge.consumables')}
      </div>
      {visibleConsumables.map(c => {
        const canUse = c.targetCount === 0 || selectedCardIds.length >= c.targetCount
        return (
          <CardShell
            key={c.id}
            size="sm"
            borderColor="rgba(212,132,154,0.35)"
            className={cn(
              'shrink-0 transition-all duration-200',
              !canUse && 'opacity-40',
            )}
            style={{
              cursor: canUse ? 'pointer' : 'not-allowed',
            }}
            title={t('challenge.consumableTooltip', { description: c.description, count: c.targetCount })}
            onClick={() => {
              if (canUse) onUse(c.id, selectedCardIds.slice(0, c.targetCount))
            }}
          >
            {/* Category dot */}
            <div
              className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full"
              style={{ background: '#D4849A' }}
            />
            {/* Icon area */}
            <div className="flex items-center justify-center pt-3 pb-1.5 px-2">
              <div
                className="w-[60px] h-[60px] rounded flex items-center justify-center"
                style={{ background: 'rgba(212,132,154,0.1)' }}
              >
                <img src={getIconifyUrl('lucide:flask-conical', 24, '#D4849A')} alt="" className="w-7 h-7" />
              </div>
            </div>
            {/* Divider */}
            <div className="mx-2.5 h-px" style={{ background: 'rgba(212,132,154,0.2)' }} />
            {/* Info */}
            <div className="flex-1 flex flex-col px-2.5 py-1.5 min-h-0">
              <div className="text-[11px] font-bold leading-tight" style={{ color: '#D4849A' }}>
                {c.name}
              </div>
              <div className="text-[9px] leading-tight mt-0.5 line-clamp-2" style={{ color: 'rgba(200,190,220,0.45)' }}>
                {c.description}
              </div>
            </div>
          </CardShell>
        )
      })}

      {/* Empty slots — max 2 total including filled */}
      {Array.from({ length: emptySlots }).map((_, i) => (
        <div
          key={`empty-${i}`}
          className="shrink-0 rounded-[10px] border-2 border-dashed opacity-20"
          style={{ width: CARD_SIZES.sm.width, height: CARD_SIZES.sm.height, borderColor: 'rgba(212,132,154,0.3)' }}
        />
      ))}
    </div>
  )
}

// =============================================================================
// ContinueButton
// =============================================================================

function ContinueButton({ onClick, delay = 200 }: { onClick: () => void; delay?: number }) {
  const { t } = useTranslation()
  return (
    <button
      className="py-2.5 px-8 border-none rounded-md text-sm font-bold cursor-pointer"
      style={{
        background: 'linear-gradient(135deg, #3D8B4F, #2D7040)',
        color: '#D4FFDD',
        boxShadow: '0 4px 12px rgba(61,139,79,0.3)',
        border: '2px solid rgba(100,200,120,0.3)',
        animation: `fade-in 300ms ease-out ${delay}ms both`,
      }}
      onClick={onClick}
    >
      {t('challenge.continue')}
    </button>
  )
}
